import Title from "@helsenorge/designsystem-react/components/Title";

import { ReferanserComp } from "./ReferanserComp";
import { ExternOppgaveVisning } from "./ExternOppgaveVisning";
import KanIkkeUtfore from "./kanIkkeUtfore";
import { KontaktInformasjonComponent } from "./kontaktInformasjon";
import SkjemaMicroweb from "./SkjemaMicroweb";
import { UtforOppgaveVisning } from "./UtforOppgaveVisning";
import { UtfortOppgaveVisning } from "./UtfortOppgaveVisning";

import { useAppDispatch, useAppSelector } from "../../store/reducer";
import { useEffect, useRef } from "react";
import { fetchOppgave } from "../../store/thunks/thunk-oppgaveDetaljer";
import { useParams } from "react-router-dom";
import styles from "../../styles/oppgave-detaljer.module.scss";
import { FullmaktToggle } from "./FullmaktToggle";
import { DeleteOppgave } from "./DeleteOppgave";
import { DetaljerPanel } from "./DetaljerPanel";
import { ErrorView } from "./ErrorView";
import { EndringerComponent } from "./Endringer";
import Loader from "@helsenorge/designsystem-react/components/Loader";
import { MottattDato } from "./MottattDato";
import { ErrorPanel } from "./ErrorPanel";
import { getErRepresentasjon } from "@helsenorge/framework-utils/hn-user";
import NotificationPanel from "@helsenorge/designsystem-react/components/NotificationPanel";
import { useGetResources } from "../../hooks/useGetResources";
import { getShowClientErrorMessage } from "utils/details-utils";
import { Intensjon } from "enums/intensjon";
import { PaaminnelseToggle } from "./PaaminnelseToggle";
import { sporAapningAvOppgave } from "utils/amplitude-utils";
import { OppgaveStatus } from "enums/oppgaveStatus";
import { getSkjulOppgave } from "utils/visning-utils";

type OppgaveDetaljerProps = {
  oppgaveGuidProp?: string;
  isLoadingProp?: boolean;
};

export const OppgaveDetaljer = ({
  oppgaveGuidProp,
  isLoadingProp,
}: OppgaveDetaljerProps): React.JSX.Element | null => {
  const dispatch = useAppDispatch();
  const { oppgaveGuid: oppgaveGuidParam } = useParams<{
    oppgaveGuid: string;
  }>();
  const oppgave = useAppSelector((state) => state.oppgaveDetaljer.oppgave);
  const isLoading = useAppSelector((state) => state.oppgaveDetaljer.isLoading);
  const hasLoadError = useAppSelector(
    (state) => state.oppgaveDetaljer.hasLoadError,
  );
  const loadErrorStatusCode = useAppSelector(
    (state) => state.oppgaveDetaljer.loadErrorStatusCode,
  );
  const resources = useGetResources();
  const skjulOppgave = getSkjulOppgave(oppgave?.SkjulForFullmakt);
  const erValgfri = oppgave?.Intensjon === Intensjon.Proposal;
  const trackedOppgaveIdRef = useRef<string | null>(null);
  const visPaaminnelseToggle =
    erValgfri &&
    (oppgave.Status === OppgaveStatus.Ready ||
      oppgave.Status === OppgaveStatus.InProgress);

  const handleFetchData = (oppgaveGuid: string) => {
    dispatch(fetchOppgave(oppgaveGuid));
  };
  const handleSporing = () => {
    if (!oppgave?.Id) {
      return;
    }
    if (trackedOppgaveIdRef.current === oppgave.Id) {
      return;
    }
    sporAapningAvOppgave(oppgave);
    trackedOppgaveIdRef.current = oppgave.Id;
  };

  useEffect(() => {
    if (oppgaveGuidProp) {
      handleFetchData(oppgaveGuidProp);
    } else if (oppgaveGuidParam) {
      handleFetchData(oppgaveGuidParam);
    }
  }, [dispatch, oppgaveGuidProp, oppgaveGuidParam]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    handleSporing();
  }, [oppgave]);

  const showClientErrorMessage = getShowClientErrorMessage(
    hasLoadError,
    loadErrorStatusCode,
  );

  return (
    <>
      {(isLoading || isLoadingProp) && <Loader />}
      {hasLoadError && !showClientErrorMessage && <ErrorView />}
      {showClientErrorMessage && (
        <NotificationPanel label={resources?.oppgave_ikke_tilgjengelig || ""} />
      )}
      {!isLoading && oppgave && !skjulOppgave && (
        <>
          <Title htmlMarkup="h1" appearance="title1">
            {oppgave.Tittel}
          </Title>
          <ErrorPanel />
          <DetaljerPanel />
          {oppgave.Beskrivelse && (
            <div className={`${styles.detaljer_seksjon} ${styles.tekstblokk}`}>
              {oppgave.Beskrivelse}
            </div>
          )}
          <SkjemaMicroweb />
          <UtfortOppgaveVisning />
          <ExternOppgaveVisning />
          <ReferanserComp />
          <UtforOppgaveVisning />
          <KontaktInformasjonComponent />
          <KanIkkeUtfore />
          {oppgave.HarEndringslogg && (
            <EndringerComponent
              oppgaveGuidParam={oppgaveGuidParam}
              oppgaveGuidProp={oppgaveGuidProp}
            />
          )}
          <MottattDato />
          {!getErRepresentasjon() && <FullmaktToggle />}
          {visPaaminnelseToggle && <PaaminnelseToggle />}
          {oppgave.KanSlette && <DeleteOppgave />}
        </>
      )}
    </>
  );
};
